import { useState, type CSSProperties, type ReactNode } from 'react';
import { cssVar, type TokenName } from '@going-once/tokens';

/**
 * The plain primitives (D24.3), on the Design System Brief's token names.
 *
 * **Every visual value here is a token reference.** No hex, no pixel count, no font
 * name — `tokens-only.spec.ts` enforces it rather than trusting anyone to remember, and
 * it is the reason restyling is a token swap rather than a refactor.
 *
 * **No design decisions in the APIs** (D24.4). `variant="primary"` names a role the
 * design system can redefine; `color="blue"` would freeze a decision into every call
 * site. So props take roles, never values.
 *
 * Deliberately unstyled beyond structure and state. They are meant to be replaced
 * visually and kept structurally.
 */

type Space = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
type Surface =
  | 'bg.base'
  // The admin console's top bar and side nav: a bar surface, not the page.
  | 'bg.bar'
  | 'bg.raised'
  | 'bg.sunken'
  | 'accent.default'
  | 'accent.subtle'
  | 'danger'
  | 'danger.subtle'
  // The state tints the auction banner reads as a surface, and the meta purple behind
  // the "timed auction" pill.
  | 'success.subtle'
  | 'warning.subtle'
  | 'meta'
  | 'meta.subtle';
/**
 * `accent.default` is here because the artboards set **the current bid** in the accent —
 * it is the number the screen is about, and rendering it as ordinary foreground is most
 * of why the panel read as a form. `overlay.fg.muted` is the label rank over video.
 */
type Ink =
  | 'fg.default'
  | 'fg.muted'
  | 'fg.inverse'
  | 'accent.default'
  | 'meta'
  | 'danger'
  | 'success'
  | 'warning'
  | 'live'
  | 'overlay.fg'
  | 'overlay.fg.muted';
type TypeStep = 'hero' | 'display' | 'heading' | 'title' | 'stat' | 'body' | 'label' | 'mono' | 'countdown';
type Radius = 'sm' | 'md' | 'lg' | 'full';

/**
 * **Which accent a staff console draws**: pink in the admin console, teal in the seller
 * dashboard. The one thing the shared frame does not render identically for both, and how
 * staff tell at a glance which one they are in.
 *
 * Teal is only ever drawn on a surface pinned dark — on the light bar it is 1.90:1
 * (`MAPPING_TO_CONFIRM`), which is why `SellerDashArea` wraps itself in `data-theme="dark"`.
 */
export type Accent = 'accent.default' | 'accent2.default';

const space = (step: Space): string => cssVar(`space.${step}` as TokenName);
const surface = (role: Surface): string => cssVar(`color.${role}` as TokenName);
const ink = (role: Ink): string => cssVar(`color.${role}` as TokenName);

/** An accent as a CSS value, the way `surface` and `ink` resolve theirs. */
export const accentColor = (role: Accent): string => cssVar(`color.${role}` as TokenName);

/**
 * The focus ring, defined once.
 *
 * Removing an outline without replacing it is the most common accessibility regression
 * a design system introduces, so no component gets to make that decision locally. React
 * has no `:focus-visible` in inline styles, hence the explicit handlers — which is also
 * why this lives in a primitive and not in a screen.
 */
export const focusRing = {
  outline: `${cssVar('size.focus.ring')} solid ${cssVar('color.border.focus')}`,
  outlineOffset: cssVar('size.focus.offset'),
} as const;

/** Hidden from sight, kept in the accessibility tree: a caption the page already shows, a full id behind a short one. */
export const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
} as const;

export interface BoxProps {
  children?: ReactNode;
  padding?: Space | undefined;
  background?: Surface | undefined;
  radius?: Radius | undefined;
  border?: boolean | undefined;
  shadow?: 'raised' | 'overlay' | 'glow' | 'accentGlow' | undefined;
  grow?: boolean | undefined;
  style?: CSSProperties | undefined;
}

export function Box({ children, padding, background, radius, border, shadow, grow, style }: BoxProps) {
  return (
    <div
      style={{
        ...(padding ? { padding: space(padding) } : {}),
        ...(background ? { background: surface(background) } : {}),
        ...(radius ? { borderRadius: cssVar(`radius.${radius}` as TokenName) } : {}),
        ...(border ? { border: `1px solid ${cssVar('color.border.default')}` } : {}),
        ...(shadow ? { boxShadow: cssVar(`elevation.${shadow}` as TokenName) } : {}),
        ...(grow ? { flex: 1, minWidth: 0 } : {}),
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export interface StackProps extends BoxProps {
  direction?: 'row' | 'column';
  gap?: Space;
  align?: 'start' | 'center' | 'end' | 'stretch';
  justify?: 'start' | 'center' | 'end' | 'between';
  wrap?: boolean;
}

const ALIGN = { start: 'flex-start', center: 'center', end: 'flex-end', stretch: 'stretch' } as const;
const JUSTIFY = { start: 'flex-start', center: 'center', end: 'flex-end', between: 'space-between' } as const;

export function Stack({ direction = 'column', gap = 'md', align, justify, wrap, ...box }: StackProps) {
  return (
    <Box
      {...box}
      style={{
        display: 'flex',
        flexDirection: direction,
        gap: space(gap),
        ...(align ? { alignItems: ALIGN[align] } : {}),
        ...(justify ? { justifyContent: JUSTIFY[justify] } : {}),
        ...(wrap ? { flexWrap: 'wrap' } : {}),
        ...box.style,
      }}
    />
  );
}

export interface TextProps {
  children?: ReactNode;
  step?: TypeStep;
  tone?: Ink;
  as?: 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'label';
  htmlFor?: string;
  /** Truncate to one line. Feed cards need it; it is layout, not decoration. */
  truncate?: boolean;
}

export function Text({ children, step = 'body', tone = 'fg.default', as = 'span', htmlFor, truncate }: TextProps) {
  const Tag = as;
  // `mono` is for ids read character by character. **The countdown is not one of them**:
  // the artboards set it in the interface family with tabular figures, which is a
  // different requirement — digits that do not jump as it ticks, in the voice of the
  // rest of the screen. Rendering it in a mono family made every price look like a
  // terminal.
  const monospaced = step === 'mono';
  const tabular = step === 'mono' || step === 'countdown';
  return (
    <Tag
      {...(htmlFor ? { htmlFor } : {})}
      style={{
        margin: 0,
        fontFamily: cssVar(monospaced ? 'family.mono' : 'family.base'),
        fontSize: cssVar(`type.${step}.size` as TokenName),
        fontWeight: cssVar(`type.${step}.weight` as TokenName),
        fontStyle: cssVar(`type.${step}.style` as TokenName),
        lineHeight: cssVar(`type.${step}.line` as TokenName),
        color: ink(tone),
        // ⚡ Digits must not jump as a countdown ticks — a clock people make money
        // decisions against cannot shift under the eye.
        ...(tabular ? { fontVariantNumeric: 'tabular-nums' } : {}),
        /**
         * `display: block` is what makes the other three do anything.
         *
         * `Text` renders a `<span>`, and **`overflow` and `text-overflow` do not apply to
         * an inline element** — so `truncate` silently did nothing, everywhere, until a
         * long lot title ran off the side of the live panel and through the countdown.
         * `minWidth: 0` is the flex half of the same fix: a flex item refuses to shrink
         * below its content unless told it may.
         */
        ...(truncate
          ? { display: 'block', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
          : {}),
      }}
    >
      {children}
    </Tag>
  );
}

export interface PressableProps {
  children?: ReactNode;
  onPress: () => void;
  /** A role the design system can redefine — never a color (D24.4). */
  variant?: 'primary' | 'secondary' | 'quiet' | 'danger';
  disabled?: boolean;
  busy?: boolean;
  /** Fill the width — a bottom action bar, a sheet's confirm. */
  grow?: boolean;
  /** `large` is the artboards' bar button (`size.target.large`); `default` is everything else. */
  size?: 'default' | 'large';
  type?: 'button' | 'submit';
}

const VARIANTS: Record<
  NonNullable<PressableProps['variant']>,
  { background: Surface | 'transparent'; hover: Surface | 'transparent'; ink: Ink; border: boolean }
> = {
  primary: { background: 'accent.default', hover: 'accent.default', ink: 'fg.inverse', border: false },
  secondary: { background: 'bg.raised', hover: 'bg.sunken', ink: 'fg.default', border: true },
  quiet: { background: 'transparent', hover: 'bg.raised', ink: 'fg.muted', border: false },
  danger: { background: 'danger', hover: 'danger', ink: 'fg.inverse', border: false },
};

/**
 * Every interactive state the brief asks for: default, hover, press, focus, disabled,
 * loading. They are here rather than in a stylesheet because a component that cannot
 * express its own states pushes them into screens, and then a reskin has to visit
 * every screen.
 */
export function Pressable({
  children,
  onPress,
  variant = 'secondary',
  disabled,
  busy,
  grow,
  size = 'default',
  type = 'button',
}: PressableProps) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [focused, setFocused] = useState(false);
  const look = VARIANTS[variant];
  const inert = disabled || busy;
  const background = look.background === 'transparent' ? 'transparent' : surface(look.background);
  const hovering = hovered && !inert;

  return (
    <button
      type={type === 'submit' ? 'submit' : 'button'}
      onClick={onPress}
      disabled={inert}
      aria-busy={busy ? true : undefined}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      // `:focus-visible` in a handler: a mouse click must not draw a ring, a keyboard
      // tab must.
      onFocus={(event) => setFocused(event.currentTarget.matches(':focus-visible'))}
      onBlur={() => setFocused(false)}
      style={{
        background: hovering && look.hover !== 'transparent' ? surface(look.hover) : background,
        color: ink(look.ink),
        border: look.border ? `1px solid ${cssVar('color.border.default')}` : '1px solid transparent',
        borderRadius: cssVar('radius.md'),
        padding: `${space('sm')} ${space('md')}`,
        // One-handed, in motion, outdoors: the brief puts tap target above density.
        minHeight: cssVar('size.target.min'),
        fontFamily: cssVar('family.base'),
        fontSize: cssVar('type.body.size'),
        fontWeight: cssVar('type.label.weight'),
        cursor: inert ? 'not-allowed' : 'pointer',
        opacity: inert ? 0.5 : 1,
        // The glow is how a primary action carries weight on a dark surface in this
        // design; without it the bid button reads as a flat rectangle. It is the
        // *accent* glow — pink, the color of the action — not the teal one, which
        // belongs to positive state.
        ...(variant === 'primary' && !inert ? { boxShadow: cssVar('elevation.accentGlow') } : {}),
        ...(size === 'large' ? { minHeight: cssVar('size.target.large'), fontSize: cssVar('type.heading.size') } : {}),
        ...(grow ? { width: '100%' } : {}),
        transform: pressed && !inert ? 'scale(0.98)' : 'none',
        transition: `background ${cssVar('motion.duration.fast')} ${cssVar('motion.easing.standard')}, transform ${cssVar('motion.duration.fast')} ${cssVar('motion.easing.standard')}`,
        ...(focused ? focusRing : {}),
      }}
    >
      {busy ? '…' : children}
    </button>
  );
}

export function Card({ children, padding = 'md' }: { children?: ReactNode; padding?: Space }) {
  return (
    <Box background="bg.raised" radius="lg" padding={padding} shadow="raised" border>
      {children}
    </Box>
  );
}

/**
 * A sheet — the surface that slides over the show (the brief names it as a primitive).
 *
 * Rendered inline rather than as a portal for now: there is nothing to slide over yet.
 * What it establishes is the token usage — `elevation.overlay` and the scrim — so the
 * live-show work inherits it rather than inventing a second answer.
 */
export function Sheet({ children, onDismiss }: { children?: ReactNode; onDismiss?: () => void }) {
  return (
    <Box
      style={{
        position: 'fixed',
        inset: 0,
        background: cssVar('color.overlay.scrim'),
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 10,
      }}
    >
      {/* The scrim dismisses. A sheet with no way out but a small button is the mobile
          equivalent of a dead end. */}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        style={{ position: 'absolute', inset: 0, background: 'transparent', border: 'none', cursor: 'pointer' }}
      />
      <Box
        background="bg.raised"
        padding="lg"
        shadow="overlay"
        style={{
          position: 'relative',
          width: '100%',
          // A sheet is a phone pattern: the one-decision surface the brief describes.
          // At container width it stops being a sheet and becomes a wide panel that
          // happens to be at the bottom of the screen.
          maxWidth: cssVar('size.phone'),
          borderTopLeftRadius: cssVar('radius.lg'),
          borderTopRightRadius: cssVar('radius.lg'),
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

const controlStyle = {
  width: '100%',
  boxSizing: 'border-box' as const,
  background: surface('bg.base'),
  color: ink('fg.default'),
  borderRadius: cssVar('radius.md'),
  padding: space('sm'),
  minHeight: cssVar('size.target.min'),
  fontFamily: cssVar('family.base'),
  fontSize: cssVar('type.body.size'),
};

export interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  id: string;
  placeholder?: string;
  multiline?: boolean;
  /** Shown under the field. Its presence is what makes the field look wrong. */
  problem?: string | undefined;
  /** Offered in a dropdown while typing; anything else may still be typed (free text with suggestions). */
  suggestions?: readonly string[] | undefined;
  /** Single-line only. `password` masks the input; `date` gives `YYYY-MM-DD`, or empty. */
  type?: 'text' | 'email' | 'password' | 'date';
  /** What a browser or password manager may fill (`email`, `current-password`, `one-time-code`…). */
  autoComplete?: string;
  /** Shown under the field when there is no problem: a rule worth knowing before typing. */
  note?: string;
  /** `date` only: the earliest day the picker offers. */
  min?: string;
}

/**
 * The problem or note under a field is its description (`aria-describedby`), so a screen
 * reader reads it with the field rather than leaving it to be found by browsing.
 */
export function Field({
  label,
  value,
  onChange,
  id,
  placeholder,
  multiline,
  problem,
  suggestions,
  type,
  autoComplete,
  note,
  min,
}: FieldProps) {
  const [focused, setFocused] = useState(false);
  const describedBy = problem ? `${id}-problem` : note ? `${id}-note` : undefined;
  const shared = {
    id,
    value,
    placeholder,
    autoComplete,
    'aria-describedby': describedBy,
    onChange: (event: { target: { value: string } }) => onChange(event.target.value),
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
    'aria-invalid': problem ? true : undefined,
    style: {
      ...controlStyle,
      border: `1px solid ${problem ? surface('danger') : cssVar('color.border.default')}`,
      ...(focused ? focusRing : {}),
    },
  };
  return (
    <Stack gap="xs">
      <Text as="label" step="label" tone="fg.muted" htmlFor={id}>
        {label}
      </Text>
      {multiline ? (
        <textarea {...shared} rows={4} />
      ) : (
        <input
          {...shared}
          type={type ?? 'text'}
          {...(min !== undefined && { min })}
          {...(suggestions?.length ? { list: `${id}-suggestions` } : {})}
        />
      )}
      {suggestions?.length && !multiline ? (
        <datalist id={`${id}-suggestions`}>
          {suggestions.map((suggestion) => (
            <option key={suggestion} value={suggestion} />
          ))}
        </datalist>
      ) : null}
      {problem ? (
        <span id={`${id}-problem`}>
          <Text step="label" tone="danger">
            {problem}
          </Text>
        </span>
      ) : note ? (
        <span id={`${id}-note`}>
          <Text step="label" tone="fg.muted">
            {note}
          </Text>
        </span>
      ) : null}
    </Stack>
  );
}

export interface SelectProps {
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
  /**
   * `disabled` is shown-but-unpickable. It exists because "why is my item not in the
   * list" is a worse question than "why is my item grayed out" — the second one answers
   * itself when the label says the reason.
   */
  options: SelectOption[];
  /** For a choice that changes what else is asked, say so under the control. */
  note?: string;
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Options sharing a group are drawn under it (`<optgroup>`), in the order the groups first appear. */
  group?: string;
}

/** Ungrouped options first, then each group, keeping the order the options came in. */
function grouped(options: SelectOption[]): { group?: string; options: SelectOption[] }[] {
  const loose = options.filter((option) => option.group === undefined);
  const groups = new Map<string, SelectOption[]>();
  for (const option of options) {
    if (option.group !== undefined) groups.set(option.group, [...(groups.get(option.group) ?? []), option]);
  }
  return [
    ...(loose.length > 0 ? [{ options: loose }] : []),
    ...[...groups].map(([group, members]) => ({ group, options: members })),
  ];
}

/**
 * A select, as a primitive rather than a styled `<select>` inside a screen — a screen
 * that styles its own control is a screen the reskin has to visit.
 */
export function Select({ label, id, value, onChange, options, note }: SelectProps) {
  const [focused, setFocused] = useState(false);
  return (
    <Stack gap="xs">
      <Text as="label" step="label" tone="fg.muted" htmlFor={id}>
        {label}
      </Text>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...controlStyle,
          border: `1px solid ${cssVar('color.border.default')}`,
          ...(focused ? focusRing : {}),
        }}
      >
        {grouped(options).map((set) => {
          const items = set.options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled === true}>
              {option.label}
            </option>
          ));
          return set.group === undefined ? (
            items
          ) : (
            <optgroup key={set.group} label={set.group}>
              {items}
            </optgroup>
          );
        })}
      </select>
      {note ? (
        <Text step="label" tone="fg.muted">
          {note}
        </Text>
      ) : null}
    </Stack>
  );
}

/**
 * Up to two initials from a name, the design system's way: the first letter of each of
 * the first two words. A name with no letters to take gives none, and the avatar is then
 * its fill alone — never a placeholder glyph standing in for a person.
 */
export function avatarInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => (Array.from(word)[0] ?? '').toUpperCase())
    .join('');
}

const AVATAR_SIZE = { row: 'size.avatar.row', header: 'size.avatar.header' } as const;
const AVATAR_TYPE = { row: 'label', header: 'stat' } as const;

export interface AvatarProps {
  /** Whose avatar: the initials come from it when there is no photo. */
  name: string;
  /** Initials to draw instead of the name's — an audit row's principal id has no words to take them from. */
  initials?: string | undefined;
  /** The photo (`avatarUrl`). One that fails to load falls back to the initials. */
  src?: string | undefined;
  /** `row` (32) in a table, `header` (56) on a record page's header. */
  size?: 'row' | 'header';
  /** The accent ring a person wears while they are live. */
  ring?: boolean;
  /**
   * They are live now: the ring, a Live badge under the avatar, and the whole of it a link
   * to the show in a new tab. `label` names the link — the badge alone says only "Live".
   */
  live?: { href: string; label: string } | undefined;
}

/**
 * A person: their photo, else their initials (the design system's `Avatar`).
 *
 * **A rounded square, everywhere** — Kelli's "Option B" (2026-10-12), which replaced the
 * circle the audit log first drew. Decorative: the name beside it is what a screen reader
 * reads, so it is hidden from one — except as a live link, whose label says whose show it is.
 */
export function Avatar({ name, initials, src, size = 'row', ring, live }: AvatarProps) {
  // Which photo failed, not whether one did: a new `src` gets its own chance to load.
  const [failed, setFailed] = useState<string | undefined>(undefined);
  const [focused, setFocused] = useState(false);
  const photo = src !== undefined && src !== '' && src !== failed;
  const edge = cssVar('size.avatar.ring');
  const face = (
    <span
      aria-hidden="true"
      style={{
        flexShrink: 0,
        display: 'inline-grid',
        placeItems: 'center',
        overflow: 'hidden',
        width: cssVar(AVATAR_SIZE[size]),
        height: cssVar(AVATAR_SIZE[size]),
        borderRadius: cssVar('radius.sm'),
        background: surface(photo ? 'bg.raised' : 'meta.subtle'),
        color: ink('meta'),
        fontFamily: cssVar('family.base'),
        fontSize: cssVar(`type.${AVATAR_TYPE[size]}.size`),
        fontWeight: cssVar('type.title.weight'),
        lineHeight: 1,
        // Two rings, as the design system draws them: the page's own color, then the accent.
        ...(ring || live
          ? {
              boxShadow: `0 0 0 ${edge} ${cssVar('color.bg.base')}, 0 0 0 calc(2 * ${edge}) ${cssVar('color.accent.default')}`,
            }
          : {}),
      }}
    >
      {photo ? (
        <img
          src={src}
          alt=""
          onError={() => setFailed(src)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        (initials ?? avatarInitials(name))
      )}
    </span>
  );
  if (!live) return face;
  return (
    <a
      href={live.href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={live.label}
      title="Watch live"
      onFocus={(event) => setFocused(event.currentTarget.matches(':focus-visible'))}
      onBlur={() => setFocused(false)}
      style={{
        position: 'relative',
        display: 'inline-flex',
        flexShrink: 0,
        // A link's underline would run under the initials.
        textDecoration: 'none',
        borderRadius: cssVar('radius.sm'),
        // Room under the avatar for the badge that hangs off its bottom edge.
        marginBottom: cssVar('space.sm'),
        ...(focused ? focusRing : {}),
      }}
    >
      {face}
      <span
        style={{
          position: 'absolute',
          left: '50%',
          bottom: `calc(-1 * ${cssVar('space.sm')})`,
          transform: 'translateX(-50%)',
          display: 'flex',
          borderRadius: cssVar('radius.sm'),
          boxShadow: `0 0 0 ${edge} ${cssVar('color.bg.base')}`,
        }}
      >
        <Badge variant="live">Live</Badge>
      </span>
    </a>
  );
}

export type BadgeVariant = 'leading' | 'timed' | 'quiet' | 'live';

const BADGES: Record<
  BadgeVariant,
  { background: 'live' | 'meta.subtle' | 'success.subtle' | 'neutral.tint'; ink: Ink; border?: 'neutral.tint.border' }
> = {
  live: { background: 'live', ink: 'fg.inverse' },
  timed: { background: 'meta.subtle', ink: 'meta' },
  leading: { background: 'success.subtle', ink: 'success' },
  quiet: { background: 'neutral.tint', ink: 'fg.muted', border: 'neutral.tint.border' },
};

/**
 * A small caps tag: a status in a table cell or beside a page title (the design system's
 * `Badge`). Four variants, by what they mean rather than their color: `live` (on air now,
 * with a dot), `timed` (a timed auction, not live), `leading` (good standing, with ▲ so it
 * is not carried by color alone) and `quiet` (everything else).
 *
 * **No size prop.** The design passes `size="sm"` in one place and the design system has
 * no such prop, so it draws nothing different — neither does this.
 */
export function Badge({ variant, children }: { variant: BadgeVariant; children: ReactNode }) {
  const look = BADGES[variant];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: space('xs'),
        boxSizing: 'border-box',
        minHeight: cssVar('size.badge'),
        padding: `0 ${space('sm')}`,
        borderRadius: cssVar('radius.sm'),
        border: `1px solid ${look.border ? cssVar(`color.${look.border}`) : 'transparent'}`,
        background: cssVar(`color.${look.background}`),
        color: ink(look.ink),
        fontFamily: cssVar('family.base'),
        fontSize: cssVar('type.label.size'),
        fontWeight: cssVar('type.title.weight'),
        lineHeight: cssVar('type.label.line'),
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {variant === 'live' ? (
        <span
          aria-hidden="true"
          style={{
            width: cssVar('size.live.dot'),
            height: cssVar('size.live.dot'),
            borderRadius: cssVar('radius.full'),
            background: 'currentColor',
          }}
        />
      ) : null}
      {variant === 'leading' ? <span aria-hidden="true">▲</span> : null}
      {children}
    </span>
  );
}

/**
 * ⚡ Leading / outbid, and the reason it is a component rather than a color.
 *
 * The brief is explicit: this pair must be distinguishable to a colourblind buyer
 * *without reading the label*, because it communicates whether someone is about to spend
 * money. A token alone cannot promise that — so the state travels on two channels, a
 * color and a glyph, and it does so in one place instead of at every call site.
 */
export function BidState({ state }: { state: 'leading' | 'outbid' }) {
  const leading = state === 'leading';
  return (
    <Stack direction="row" gap="xs" align="center">
      <Text step="body" tone={leading ? 'success' : 'warning'} aria-hidden>
        {leading ? '▲' : '▼'}
      </Text>
      <Text step="label" tone={leading ? 'success' : 'warning'}>
        {leading ? 'Leading' : 'Outbid'}
      </Text>
    </Stack>
  );
}
